import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_DEV ? 'http://localhost:8000' : "https://investors.deepq.live";

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Auth token management
export const setAuthToken = (token: string) => {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const clearAuthToken = () => {
  delete api.defaults.headers.common['Authorization'];
};

// Types
export interface LoginResponse {
  access_token: string;
  token_type: string;
  investor_id: string;
  is_admin: boolean;
}

export interface MonthlyReport {
  investor_id: string;
  investor_name: string;
  month?: string;
  start_date?: string;
  report_date: string;
  report_type?: string;
  starting_balance: {
    USD_BALANCE: number;
    BTC_BALANCE: number;
    ETH_BALANCE: number;
    TOTAL_USD_BALANCE: number;
  };
  ending_balance: {
    USD_BALANCE: number;
    BTC_BALANCE: number;
    ETH_BALANCE: number;
    TOTAL_USD_BALANCE: number;
  };
  monthly_pnl_usd?: number;
  monthly_fees_usd?: number;
  lifetime_pnl_usd?: number;
  lifetime_fees_usd?: number;
  net_pnl_after_fees: number;
  performance_summary: {
    total_return_percent: number;
    pnl_gross_return_percent: number;
    pnl_net_return_percent: number;
    starting_value_usd: number;
    ending_value_usd: number;
    change_in_value_usd: number;
  };
}

export interface LineGraphData {
  [date: string]: {
    total_asset_value: number;
    pnl: number;
    percentage_total_asset_growth: number;
    percentage_pnl_growth: number;
  };
}

export interface InvestorLetter {
  content: string;
  month: string;
}

export interface InvestorInfo {
  name: string;
  email: string;
  entity_type: string;
  subscription_date: string;
}

// API functions
export const login = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await api.post('/api/auth/login', { email, password });
  return response.data;
};

export const getMonthlyReport = async (investorId: string, month: string): Promise<MonthlyReport> => {
  const response = await api.get(`/api/data/monthly-report/${investorId}/${month}`);
  return response.data;
};

export const getCurrentMonthReport = async (investorId: string): Promise<MonthlyReport> => {
  const response = await api.get(`/api/data/current-month-report/${investorId}`);
  return response.data;
};

export const getInvestorLetter = async (month: string): Promise<InvestorLetter> => {
  const response = await api.get(`/api/data/investor-letter/${month}`);
  return response.data;
};

export const getInvestorInformation = async (investorId: string): Promise<InvestorInfo> => {
  const response = await api.get(`/api/data/investor-information/${investorId}`);
  return response.data;
};

export const getLineGraphData = async (investorId: string): Promise<LineGraphData> => {
  const response = await api.get(`/api/data/line-graph-data/${investorId}`);
  return response.data;
};

export const getInvestorList = async (): Promise<{ investors: string[] }> => {
  const response = await api.get('/api/data/investor-list');
  return response.data;
};

export const getAvailableMonths = async (investorId: string): Promise<{ months: string[] }> => {
  const response = await api.get(`/api/data/available-months/${investorId}`);
  return response.data;
};

export const getAvailableLetters = async (): Promise<{ letters: string[] }> => {
  const response = await api.get('/api/data/available-letters');
  return response.data;
};